import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Download, FileSpreadsheet, FileText, Search, Trash2, CheckSquare, Square, AlertTriangle } from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";

import { api } from "@/api/client";
import { useLang } from "@/context/LangContext";
import { ADMIN } from "@/constants/testIds";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

export default function Responses() {
  const { t, lang } = useLang();
  const [rows, setRows] = useState([]);
  const [exams, setExams] = useState([]);
  const [q, setQ] = useState("");
  const [examId, setExamId] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [{ data }, { data: exs }] = await Promise.all([
      api.get("/admin/attempts", { params: { employee: q || undefined, exam_id: examId === "all" ? undefined : examId } }),
      api.get("/admin/exams"),
    ]);
    setRows(data);
    setExams(exs);
    setSelected(new Set());
  };
  useEffect(() => { load(); }, [q, examId]);

  const loadDetail = async (attemptId) => {
    if (expanded?.attempt_id === attemptId) { setExpanded(null); return; }
    const { data } = await api.get(`/admin/attempts/${attemptId}`);
    setExpanded(data);
  };

  const toggle = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const allSelected = rows.length > 0 && selected.size === rows.length;
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.attempt_id)));
  };

  const deleteOne = async (attemptId, name) => {
    if (!window.confirm(lang === "es" ? `¿Eliminar respuesta de ${name}?` : `Delete response from ${name}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/attempts/${attemptId}`);
      toast.success(lang === "es" ? "Respuesta eliminada" : "Response deleted");
      if (expanded?.attempt_id === attemptId) setExpanded(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(
      lang === "es"
        ? `¿Eliminar ${selected.size} respuesta(s) seleccionada(s)? No se puede deshacer.`
        : `Delete ${selected.size} selected response(s)? Cannot be undone.`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/attempts/purge", { attempt_ids: [...selected] });
      toast.success(lang === "es" ? `${data.deleted} eliminadas` : `${data.deleted} deleted`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const deleteAllFiltered = async () => {
    const filterActive = q || examId !== "all";
    const label = filterActive
      ? (lang === "es" ? "todo el historial FILTRADO" : "the FILTERED history")
      : (lang === "es" ? "TODO el historial (sin excepciones)" : "the ENTIRE history (no exceptions)");
    if (!window.confirm(lang === "es" ? `⚠️ ¿Eliminar ${label}? Esta acción NO se puede deshacer.` : `⚠️ Delete ${label}? This CANNOT be undone.`)) return;
    // Extra safety on full purge
    if (!filterActive) {
      const confirm = window.prompt(lang === "es" ? "Escribe BORRAR para confirmar" : "Type DELETE to confirm");
      if (!confirm || (confirm.trim().toUpperCase() !== "BORRAR" && confirm.trim().toUpperCase() !== "DELETE")) {
        toast.info(lang === "es" ? "Cancelado" : "Cancelled");
        return;
      }
    }
    setBusy(true);
    try {
      const body = filterActive
        ? { employee: q || undefined, exam_id: examId === "all" ? undefined : examId }
        : { all: true };
      const { data } = await api.post("/admin/attempts/purge", body);
      toast.success(lang === "es" ? `${data.deleted} eliminadas` : `${data.deleted} deleted`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const exportExcel = () => {
    const data = rows.map((r) => ({
      Employee: r.employee_name,
      Exam: lang === "es" ? r.exam_title_es : r.exam_title_en,
      Score: `${r.score_correct}/${r.score_total}`,
      Percent: r.score_percent,
      Passed: r.passed ? "YES" : "NO",
      Submitted: r.submitted_at,
      Duration_s: r.duration_seconds,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Responses");
    XLSX.writeFile(wb, `canales-responses-${Date.now()}.xlsx`);
  };

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(16);
    doc.text("Canales Furniture — Exam Responses", 14, 15);
    doc.setFontSize(10);
    let y = 25;
    doc.text("Employee    |  Exam    |  Score    |  Passed  |  Date", 14, y);
    y += 6;
    rows.forEach((r) => {
      if (y > 190) { doc.addPage(); y = 20; }
      const line = `${r.employee_name} | ${(lang === "es" ? r.exam_title_es : r.exam_title_en).slice(0, 30)} | ${r.score_correct}/${r.score_total} (${r.score_percent}%) | ${r.passed ? "YES" : "NO"} | ${(r.submitted_at || "").slice(0, 19)}`;
      doc.text(line, 14, y);
      y += 6;
    });
    doc.save(`canales-responses-${Date.now()}.pdf`);
  };

  const filterLabel = useMemo(() => {
    if (q || examId !== "all") return lang === "es" ? "filtro activo" : "filtered";
    return lang === "es" ? "todo el historial" : "all history";
  }, [q, examId, lang]);

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display font-black text-4xl">{t("responses")}</h1>
        <div className="flex flex-wrap gap-2">
          <Button data-testid={ADMIN.exportExcel} onClick={exportExcel} className="brutal-btn bg-green-500 text-white px-4 py-2">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> {t("exportExcel")}
          </Button>
          <Button data-testid={ADMIN.exportPdf} onClick={exportPDF} className="brutal-btn bg-canales-red text-white px-4 py-2">
            <FileText className="w-4 h-4 mr-1" /> {t("exportPdf")}
          </Button>
        </div>
      </div>

      <div className="brutal p-4 bg-white flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs font-display font-bold uppercase">{t("employees")}</label>
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-neutral-400" />
            <Input data-testid={ADMIN.filterEmployee} value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" placeholder={t("yourName")} />
          </div>
        </div>
        <div className="min-w-[220px]">
          <label className="text-xs font-display font-bold uppercase">{t("exams")}</label>
          <Select value={examId} onValueChange={setExamId}>
            <SelectTrigger data-testid={ADMIN.filterExam}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all")}</SelectItem>
              {exams.map((e) => (
                <SelectItem key={e.exam_id} value={e.exam_id}>{lang === "es" ? e.title_es : e.title_en}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Bulk actions bar */}
      <div className="brutal p-3 bg-white flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={toggleAll}
          data-testid="responses-select-all"
          className="brutal-btn bg-white px-3 py-1 text-sm flex items-center gap-2"
        >
          {allSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
          {allSelected
            ? (lang === "es" ? "Quitar selección" : "Unselect")
            : (lang === "es" ? "Seleccionar todo" : "Select all")}
        </button>
        <div className="text-sm font-body font-bold">
          {selected.size} {lang === "es" ? "seleccionada(s)" : "selected"} · {rows.length} {lang === "es" ? "visibles" : "shown"}
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button
            disabled={busy || selected.size === 0}
            onClick={deleteSelected}
            data-testid="responses-delete-selected"
            className="brutal-btn bg-red-500 text-white px-3 py-1 text-sm flex items-center gap-1 disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" /> {lang === "es" ? "Eliminar seleccionadas" : "Delete selected"}
          </Button>
          <Button
            disabled={busy || rows.length === 0}
            onClick={deleteAllFiltered}
            data-testid="responses-delete-all"
            className="brutal-btn bg-canales-navy text-white px-3 py-1 text-sm flex items-center gap-1 disabled:opacity-50"
            style={{ background: "#0d1a3f" }}
          >
            <AlertTriangle className="w-4 h-4" /> {lang === "es" ? `Borrar ${filterLabel}` : `Delete ${filterLabel}`}
          </Button>
        </div>
      </div>

      <div className="brutal bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8"></TableHead>
              <TableHead className="font-display">{lang === "es" ? "Empleado" : "Employee"}</TableHead>
              <TableHead className="font-display">{lang === "es" ? "Examen" : "Exam"}</TableHead>
              <TableHead className="font-display">{lang === "es" ? "Puntaje" : "Score"}</TableHead>
              <TableHead className="font-display">%</TableHead>
              <TableHead className="font-display">{lang === "es" ? "Estado" : "Status"}</TableHead>
              <TableHead className="font-display">{lang === "es" ? "Fecha" : "Date"}</TableHead>
              <TableHead className="w-14"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-neutral-500 py-6">{t("noAttempts")}</TableCell></TableRow>
            )}
            {rows.map((r) => (
              <React.Fragment key={r.attempt_id}>
                <TableRow className="hover:bg-canales-cream">
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selected.has(r.attempt_id)}
                      onCheckedChange={() => toggle(r.attempt_id)}
                      data-testid={`responses-row-check-${r.attempt_id}`}
                    />
                  </TableCell>
                  <TableCell className="font-display font-bold cursor-pointer" onClick={() => loadDetail(r.attempt_id)}>{r.employee_name}</TableCell>
                  <TableCell className="cursor-pointer" onClick={() => loadDetail(r.attempt_id)}>{lang === "es" ? r.exam_title_es : r.exam_title_en}</TableCell>
                  <TableCell className="cursor-pointer" onClick={() => loadDetail(r.attempt_id)}>{r.score_correct}/{r.score_total}</TableCell>
                  <TableCell className="cursor-pointer" onClick={() => loadDetail(r.attempt_id)}>{r.score_percent}%</TableCell>
                  <TableCell className="cursor-pointer" onClick={() => loadDetail(r.attempt_id)}>
                    <span className={`brutal-sm px-2 py-0.5 text-xs font-display font-bold ${r.passed ? "bg-green-200" : "bg-red-200"}`}>
                      {r.passed ? t("passed") : t("failed")}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs cursor-pointer" onClick={() => loadDetail(r.attempt_id)}>{(r.submitted_at || "").slice(0, 19).replace("T", " ")}</TableCell>
                  <TableCell>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteOne(r.attempt_id, r.employee_name); }}
                      data-testid={`responses-row-delete-${r.attempt_id}`}
                      title={lang === "es" ? "Eliminar" : "Delete"}
                      className="p-1.5 text-red-600 hover:text-white hover:bg-red-500 rounded border-2 border-transparent hover:border-black transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </TableCell>
                </TableRow>
                {expanded?.attempt_id === r.attempt_id && (
                  <TableRow>
                    <TableCell colSpan={8} className="bg-canales-cream">
                      <div className="p-3 space-y-2">
                        {expanded.answers.map((ans) => {
                          const q2 = expanded.questions[ans.question_id];
                          if (!q2) return null;
                          const correctOpt = q2.options.find((o) => o.id === q2.correct);
                          const pickedOpt = q2.options.find((o) => o.id === ans.selected);
                          return (
                            <div key={ans.question_id} className={`brutal-sm p-3 ${ans.correct ? "bg-green-50" : "bg-red-50"}`}>
                              <div className="text-xs font-display font-bold uppercase text-canales-red">{q2.topic}</div>
                              <div className="font-body">{q2[`prompt_${lang}`]}</div>
                              <div className="text-sm mt-1">
                                <span className="font-bold">{t("yourAnswer")} </span>
                                {pickedOpt ? `${pickedOpt.id}. ${pickedOpt[lang]}` : t("unanswered")}
                              </div>
                              {!ans.correct && (
                                <div className="text-sm mt-1 text-green-700">
                                  <span className="font-bold">{t("correctAnswer")} </span>
                                  {correctOpt ? `${correctOpt.id}. ${correctOpt[lang]}` : ""}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
    </motion.div>
  );
}
